// src/utils/awsEvidenceContext.js
// Pure AWS/IAM evidence helpers for Compliance.jsx.
// No API calls, state writes, or React dependencies.

import {
  cleanValue,
  normalizeText,
} from "./complianceFormatters";

export function asObject(value) {
  if (!value) return {};
  if (typeof value === "object" && !Array.isArray(value)) return value;
  return {};
}

export function getDeepValue(source, paths = []) {
  const root = asObject(source);

  for (const path of paths) {
    let current = root;
    let found = true;

    for (const key of path) {
      if (!current || typeof current !== "object" || !(key in current)) {
        found = false;
        break;
      }
      current = current[key];
    }

    if (found && current !== undefined && current !== null && String(current).trim() !== "") {
      return current;
    }
  }

  return null;
}

export function policyNameFromArn(policyArn) {
  const cleaned = cleanValue(policyArn, "");
  if (!cleaned || cleaned === "—") return "";
  return cleaned.includes("/") ? cleaned.split("/").pop() : cleaned;
}

export function parseIamCredentialResourceId(resourceId) {
  const cleaned = cleanValue(resourceId, "");
  if (!cleaned || cleaned === "—" || !cleaned.includes(":")) {
    return { userName: "", accessKeySlot: "" };
  }

  const parts = cleaned.split(":");
  const possibleSlot = cleanValue(parts.pop(), "");
  const userName = cleanValue(parts.join(":"), "");
  const normalizedSlot = normalizeText(possibleSlot);

  if (!["key1", "key2", "access_key_1", "access_key_2"].includes(normalizedSlot)) {
    return { userName: cleaned, accessKeySlot: "" };
  }

  const accessKeySlot = normalizedSlot.includes("2") ? "key2" : "key1";
  return { userName, accessKeySlot };
}

export function isIamAccessKeyRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  return rule.includes("ACCESS_KEY_1") || rule.includes("ACCESS_KEY_2") || rule.includes("ACCESS_KEY");
}

export function isIamCredentialReportRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  return (
    isIamAccessKeyRule(rule) ||
    rule.includes("PASSWORD_UNUSED") ||
    rule.includes("USER_UNUSED") ||
    rule.includes("IAM_USER_MFA") ||
    rule.includes("IAM_USER_HAS_NO_MFA_AND_ACTIVE_KEY") ||
    rule.includes("NO_MFA_AND_ACTIVE_KEY")
  );
}

export function isS3CurrentStateRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();
  return (
    rule.includes("S3_VERSIONING") ||
    rule.includes("S3_LOGGING") ||
    rule.includes("S3_ENCRYPTION") ||
    rule.includes("S3_PUBLIC_ACCESS_BLOCK") ||
    rule.includes("S3_POLICY_PUBLIC")
  );
}

export function getFullAssessmentPlanForRule(ruleId = "") {
  const rule = String(ruleId || "").toUpperCase();

  if (isS3CurrentStateRule(rule)) {
    return {
      syncStream: "aws_s3_posture",
      syncLabel: "AWS S3 posture",
      normalizeStream: "aws_s3_posture",
      normalizeLabel: "AWS S3 posture",
      postureRunNormalizationFirst: false,
      validationDetail:
        "OASYS is syncing direct S3 posture evidence, refreshing bucket findings, recalculating posture, and checking ready remediation tasks.",
    };
  }

  if (isIamCredentialReportRule(rule)) {
    return {
      syncStream: "aws_iam_inventory",
      syncLabel: "AWS IAM inventory",
      normalizeStream: "aws_iam_inventory",
      normalizeLabel: "AWS IAM inventory",
      postureRunNormalizationFirst: false,
      validationDetail:
        "OASYS is syncing IAM inventory, refreshing credential-report findings, recalculating posture, and checking ready remediation tasks.",
    };
  }

  return {
    syncStream: "aws_config_resources",
    syncLabel: "AWS Config",
    normalizeStream: "",
    normalizeLabel: "",
    postureRunNormalizationFirst: true,
    validationDetail:
      "OASYS is syncing source evidence, recalculating posture, and checking ready remediation tasks.",
  };
}

export function getAccessKeySlotLabel(accessKeySlot = "", ruleId = "") {
  const slot = normalizeText(accessKeySlot);
  const rule = String(ruleId || "").toUpperCase();

  if (slot.includes("2") || rule.includes("ACCESS_KEY_2")) return "Access key 2";
  if (slot.includes("1") || rule.includes("ACCESS_KEY_1")) return "Access key 1";
  return "Access key";
}

export function getCredentialIssueLabel(ruleId = "", accessKeySlot = "") {
  const rule = String(ruleId || "").toUpperCase();
  const slotLabel = getAccessKeySlotLabel(accessKeySlot, ruleId);

  if (rule.includes("OLD_90D")) return `${slotLabel} older than 90 days`;
  if (rule.includes("NEVER_USED")) return `${slotLabel} has never been used`;
  if (rule.includes("ACTIVE")) return `${slotLabel} is active`;
  if (rule.includes("PASSWORD_UNUSED")) return "Console password unused for 90 days";
  if (rule.includes("USER_UNUSED")) return "IAM user appears unused";
  if (rule.includes("IAM_USER_HAS_NO_MFA_AND_ACTIVE_KEY") || rule.includes("NO_MFA_AND_ACTIVE_KEY")) {
    return "IAM user has no MFA and an active access key";
  }
  if (rule.includes("IAM_USER_MFA")) return "IAM user MFA is not enabled";
  return "IAM credential issue";
}

export function getAwsEvidenceContext(item = {}, task = {}) {
  const raw = asObject(item?.raw);
  const taskMetadata = asObject(task?.task_metadata);
  const lastValidation = asObject(taskMetadata?.last_validation);
  const currentStateValidation = asObject(lastValidation?.current_state_validation);

  const directFindingMetadata = asObject(item?.finding_metadata);
  const rawFindingMetadata = asObject(raw?.finding_metadata);
  const directMetadata = asObject(item?.metadata);
  const rawMetadata = asObject(raw?.metadata);
  const rawDetails = asObject(raw?.details);
  const taskAwsContext = asObject(taskMetadata?.aws_context);
  const itemAwsContext = asObject(item?.aws_context);
  const rawAwsContext = asObject(raw?.aws_context);

  const cloudTrailEventCandidates = [
    directFindingMetadata?.raw_payload?.CloudTrailEvent,
    rawFindingMetadata?.raw_payload?.CloudTrailEvent,
    directMetadata?.raw_payload?.CloudTrailEvent,
    rawMetadata?.raw_payload?.CloudTrailEvent,
    raw?.raw_payload?.CloudTrailEvent,
  ];

  const parsedCloudTrailEvents = cloudTrailEventCandidates
    .map((candidate) => {
      if (!candidate) return {};
      if (typeof candidate === "object") return candidate;
      try {
        return JSON.parse(candidate);
      } catch (_error) {
        return {};
      }
    })
    .filter((candidate) => candidate && typeof candidate === "object" && !Array.isArray(candidate));

  const candidates = [
    currentStateValidation,
    taskAwsContext,
    itemAwsContext,
    rawAwsContext,
    directFindingMetadata,
    rawFindingMetadata,
    directMetadata,
    rawMetadata,
    rawDetails,
    item,
    raw,
    ...parsedCloudTrailEvents,
  ].map(asObject);

  const read = (paths) => {
    for (const candidate of candidates) {
      const value = getDeepValue(candidate, paths);
      if (value !== null && value !== undefined && String(value).trim() !== "") {
        return value;
      }
    }
    return null;
  };

  const rule = String(item?.rule_id || item?.ruleId || task?.rule_id || "").toUpperCase();
  const resourceId = cleanValue(
    read([
      ["resource_id"],
      ["resourceId"],
      ["resource", "id"],
    ]) ||
      item?.resource_id ||
      item?.resourceId ||
      raw?.resource_id ||
      raw?.resourceId ||
      task?.resource_id ||
      task?.resourceId,
    ""
  );
  const credentialResource = parseIamCredentialResourceId(resourceId);
  const accessKeySlot = cleanValue(
    read([
      ["access_key_slot"],
      ["accessKeySlot"],
      ["key_slot"],
      ["credential_key"],
      ["credentialKey"],
      ["access_key_index"],
      ["accessKeyIndex"],
    ]) || credentialResource.accessKeySlot,
    ""
  );
  const credentialIssue = cleanValue(
    read([
      ["credential_issue"],
      ["credentialIssue"],
      ["issue"],
    ]) || (isIamCredentialReportRule(rule) ? getCredentialIssueLabel(rule, accessKeySlot) : ""),
    ""
  );

  const credentialKeyNumber =
    normalizeText(accessKeySlot).includes("2") || rule.includes("ACCESS_KEY_2")
      ? "2"
      : normalizeText(accessKeySlot).includes("1") || rule.includes("ACCESS_KEY_1")
      ? "1"
      : "";

  const readCredentialField = (fieldName) =>
    read([
      [fieldName],
      ["raw_payload", fieldName],
      ["credential_report", fieldName],
      ["credentialReport", fieldName],
      ["aws", fieldName],
    ]);

  const accessKeyActive = credentialKeyNumber
    ? readCredentialField(`access_key_${credentialKeyNumber}_active`)
    : null;
  const accessKeyLastRotated = credentialKeyNumber
    ? readCredentialField(`access_key_${credentialKeyNumber}_last_rotated`)
    : null;
  const accessKeyLastUsedDate = credentialKeyNumber
    ? readCredentialField(`access_key_${credentialKeyNumber}_last_used_date`)
    : null;
  const accessKeyLastUsedRegion = credentialKeyNumber
    ? readCredentialField(`access_key_${credentialKeyNumber}_last_used_region`)
    : null;
  const accessKeyLastUsedService = credentialKeyNumber
    ? readCredentialField(`access_key_${credentialKeyNumber}_last_used_service`)
    : null;

  const roleName = cleanValue(
    read([
      ["role_name"],
      ["roleName"],
      ["request_parameters", "roleName"],
      ["requestParameters", "roleName"],
      ["request_parameters", "role_name"],
      ["requestParameters", "role_name"],
      ["aws", "roleName"],
      ["aws", "role_name"],
    ]) ||
      (normalizeText(item?.resource_type || raw?.resource_type) === "iam_role"
        ? item?.resource_id || raw?.resource_id
        : "") ||
      (normalizeText(task?.resource_type) === "iam_role" ? task?.resource_id : ""),
    ""
  );

  const userName = cleanValue(
    read([
      ["user_name"],
      ["userName"],
      ["username"],
      ["request_parameters", "userName"],
      ["requestParameters", "userName"],
      ["request_parameters", "user_name"],
      ["requestParameters", "user_name"],
      ["aws", "userName"],
      ["aws", "user_name"],
    ]) ||
      credentialResource.userName ||
      (normalizeText(item?.resource_type || raw?.resource_type) === "iam_user"
        ? item?.resource_id || raw?.resource_id
        : "") ||
      (normalizeText(task?.resource_type) === "iam_user" ? task?.resource_id : ""),
    ""
  );

  const groupName = cleanValue(
    read([
      ["group_name"],
      ["groupName"],
      ["request_parameters", "groupName"],
      ["requestParameters", "groupName"],
      ["request_parameters", "group_name"],
      ["requestParameters", "group_name"],
      ["aws", "groupName"],
      ["aws", "group_name"],
    ]) ||
      (normalizeText(item?.resource_type || raw?.resource_type) === "iam_group"
        ? item?.resource_id || raw?.resource_id
        : ""),
    ""
  );

  const roleArn = cleanValue(
    read([
      ["role_arn"],
      ["roleArn"],
      ["identity_arn"],
      ["identityArn"],
      ["aws", "roleArn"],
      ["aws", "role_arn"],
    ]),
    ""
  );

  const userArn = cleanValue(
    read([
      ["user_arn"],
      ["userArn"],
      ["identity_arn"],
      ["identityArn"],
      ["aws", "userArn"],
      ["aws", "user_arn"],
    ]),
    ""
  );

  const policyArn = cleanValue(
    read([
      ["policy_arn"],
      ["policyArn"],
      ["request_parameters", "policyArn"],
      ["requestParameters", "policyArn"],
      ["request_parameters", "policy_arn"],
      ["requestParameters", "policy_arn"],
      ["aws", "policyArn"],
      ["aws", "policy_arn"],
    ]),
    ""
  );

  const policyName = cleanValue(
    read([
      ["policy_name"],
      ["policyName"],
      ["request_parameters", "policyName"],
      ["requestParameters", "policyName"],
      ["request_parameters", "policy_name"],
      ["requestParameters", "policy_name"],
      ["aws", "policyName"],
      ["aws", "policy_name"],
    ]) || policyNameFromArn(policyArn),
    ""
  );

  const policyDocument = cleanValue(
    read([
      ["policy_document"],
      ["policyDocument"],
      ["request_parameters", "policyDocument"],
      ["requestParameters", "policyDocument"],
    ]),
    ""
  );

  const eventName = cleanValue(
    read([
      ["event_name"],
      ["eventName"],
      ["EventName"],
      ["raw_event", "eventName"],
      ["cloudtrail", "eventName"],
    ]),
    ""
  );

  const actorName = cleanValue(
    read([
      ["actor"],
      ["actorName"],
      ["username"],
      ["Username"],
      ["user_name"],
      ["userIdentity", "userName"],
      ["userIdentity", "arn"],
      ["raw_event", "userIdentity", "userName"],
      ["cloudtrail", "userIdentity", "userName"],
    ]),
    ""
  );

  const eventTime = cleanValue(
    read([
      ["event_time"],
      ["eventTime"],
      ["EventTime"],
      ["raw_event", "eventTime"],
      ["cloudtrail", "eventTime"],
    ]),
    ""
  );

  const sourceTable = cleanValue(
    read([
      ["source_table"],
      ["sourceTable"],
      ["source", "table"],
    ]),
    ""
  );

  const validationMethod = cleanValue(
    read([
      ["validation_method"],
      ["validationMethod"],
    ]),
    ""
  );

  const source = cleanValue(
    read([
      ["source"],
      ["event_source"],
      ["eventSource"],
      ["EventSource"],
    ]),
    ""
  );

  const identityType = cleanValue(
    read([["identity_type"], ["identityType"]]) ||
      (roleName ? "role" : userName ? "user" : groupName ? "group" : ""),
    ""
  );

  const identityName = cleanValue(
    read([["identity_name"], ["identityName"]]) ||
      roleName ||
      userName ||
      groupName,
    ""
  );

  const attachedPolicyArns = Array.isArray(currentStateValidation?.attached_policy_arns)
    ? currentStateValidation.attached_policy_arns
    : [];
  const attachedPolicyNames = Array.isArray(currentStateValidation?.attached_policy_names)
    ? currentStateValidation.attached_policy_names
    : [];
  const inlinePolicyNames = Array.isArray(currentStateValidation?.inline_policy_names)
    ? currentStateValidation.inline_policy_names
    : [];

  const hasAnyValue = Boolean(
    identityName ||
      roleName ||
      roleArn ||
      userName ||
      userArn ||
      groupName ||
      policyArn ||
      policyName ||
      eventName ||
      actorName ||
      eventTime ||
      sourceTable ||
      source ||
      resourceId ||
      accessKeySlot ||
      credentialIssue ||
      accessKeyActive !== null ||
      accessKeyLastRotated ||
      accessKeyLastUsedDate ||
      accessKeyLastUsedRegion ||
      accessKeyLastUsedService
  );

  if (!hasAnyValue) return null;

  let awsPath = cleanValue(read([["aws_path"], ["awsPath"]]), "");

  // Credential-report findings can arrive with a generic AWS path from older
  // normalized context. For these rules, the safest UX is to force the exact
  // IAM credential path once we know the affected user/key slot so operators do
  // not see the misleading Permissions path.
  if (isIamAccessKeyRule(rule) && userName) {
    awsPath = `IAM → Users → ${userName} → Security credentials → Access keys`;
  } else if (
    (rule.includes("PASSWORD_UNUSED") ||
      rule.includes("USER_UNUSED") ||
      rule.includes("IAM_USER_MFA") ||
      rule.includes("IAM_USER_HAS_NO_MFA_AND_ACTIVE_KEY") ||
      rule.includes("NO_MFA_AND_ACTIVE_KEY")) &&
    userName
  ) {
    awsPath = `IAM → Users → ${userName} → Security credentials`;
  } else if (!awsPath) {
    if (roleName) {
      awsPath = `IAM → Roles → ${roleName} → Permissions`;
    } else if (userName) {
      awsPath = `IAM → Users → ${userName} → Permissions`;
    } else if (groupName) {
      awsPath = `IAM → User groups → ${groupName} → Permissions`;
    } else if (isIamCredentialReportRule(rule)) {
      awsPath = "IAM → Users → affected user → Security credentials";
    } else {
      awsPath = "IAM → affected identity → Permissions";
    }
  }

  if (rule.includes("INLINE_POLICY_ADDED_EVENT") && !awsPath.includes("Inline policies")) {
    awsPath = `${awsPath} → Inline policies`;
  }

  return {
    identityType,
    identityName,
    roleName,
    roleArn,
    userName,
    userArn,
    groupName,
    policyArn,
    policyName,
    policyDocument,
    eventName,
    actorName,
    eventTime,
    source,
    sourceTable,
    validationMethod,
    resourceId,
    accessKeySlot,
    credentialIssue,
    accessKeyActive,
    accessKeyLastRotated,
    accessKeyLastUsedDate,
    accessKeyLastUsedRegion,
    accessKeyLastUsedService,
    attachedPolicyArns,
    attachedPolicyNames,
    inlinePolicyNames,
    awsPath,
  };
}
